import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import React, { useState, useEffect, useRef } from "react";
import { createClient } from "@supabase/supabase-js";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import WhatsAppButton from "../../common/WhatsAppButton";
import "./Contact.css";

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

gsap.registerPlugin(ScrollTrigger);

function ContactUs() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

  const sectionRef = useRef(null);
  const cardRef = useRef(null);
  const headerRef = useRef(null);
  const formFieldsRef = useRef([]);
  const infoCardsRef = useRef([]);

  // Contact information
  const contactInfo = {
    email: "info@entrepreneursinstitute.org.za",
    phone: "+27100257836",
    phoneDisplay: "010 025 7836",
    whatsapp: "27730957508",
    address: "Fourways Golf Park, 32 Roos Street, Fourways, 2191",
  };

  useEffect(() => {
    // Header animation
    gsap.fromTo(
      headerRef.current,
      { y: 80, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 85%",
          toggleActions: "play none none reverse",
        },
      }
    );

    // Card entrance
    gsap.fromTo(
      cardRef.current,
      { y: 100, opacity: 0, scale: 0.9 },
      {
        y: 0,
        opacity: 1,
        scale: 1,
        duration: 1,
        ease: "back.out(1.5)",
        scrollTrigger: {
          trigger: cardRef.current,
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      }
    );

    // Info cards stagger
    gsap.fromTo(
      infoCardsRef.current,
      { x: -50, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.2,
        ease: "power2.out",
        scrollTrigger: {
          trigger: cardRef.current,
          start: "top 75%",
          toggleActions: "play none none reverse",
        },
      }
    );

    // Form fields stagger
    gsap.fromTo(
      formFieldsRef.current,
      { x: 50, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.15,
        ease: "power2.out",
        scrollTrigger: {
          trigger: cardRef.current,
          start: "top 70%",
          toggleActions: "play none none reverse",
        },
      }
    );
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const { error } = await supabase.from('contact_submissions').insert({
      name,
      email,
      message,
      subject: 'Intek Solutions enquiry',
      source: 'intek',
    });

    if (!error) {
      setSubmitStatus("success");

      gsap.fromTo(
        ".success-message",
        { scale: 0, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.5, ease: "back.out(2)" }
      );

      setTimeout(() => {
        setName("");
        setEmail("");
        setMessage("");
        setSubmitStatus(null);
      }, 3000);
    } else {
      setSubmitStatus("error");

      gsap.to(cardRef.current, {
        x: -10,
        duration: 0.1,
        repeat: 5,
        yoyo: true,
        ease: "power1.inOut",
      });

      setTimeout(() => {
        setSubmitStatus(null);
      }, 3000);
    }

    setIsSubmitting(false);
  };

  const handleFieldFocus = (index) => {
    const field = formFieldsRef.current[index];
    if (field) {
      gsap.to(field, {
        scale: 1.02,
        boxShadow: "0 0 20px rgba(105, 54, 244, 0.3)",
        duration: 0.3,
        ease: "power2.out",
      });
    }
  };

  const handleFieldBlur = (index) => {
    const field = formFieldsRef.current[index];
    if (field) {
      gsap.to(field, {
        scale: 1,
        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
        duration: 0.3,
        ease: "power2.out",
      });
    }
  };

  return (
    <section
      ref={sectionRef}
      className="contact-section"
      id="contact1"
      style={{
        position: "relative",
        overflow: "hidden",
        background: "linear-gradient(180deg, #0F0C1F 0%, #0F0C1F 50%, #f7f7f7 50%, #f7f7f7 100%)",
        paddingTop: "6rem",
        paddingBottom: "6rem",
      }}
    >
      {/* Background Decoration - Dark Section */}
      <div className="contact-bg-decoration contact-bg-dark">
        <div className="contact-bg-circle contact-bg-circle-1"></div>
        <div className="contact-grid-pattern"></div>
      </div>

      {/* Background Decoration - Light Section */}
      <div className="contact-bg-decoration contact-bg-light">
        <div className="contact-bg-circle contact-bg-circle-2"></div>
      </div>

      <Container className="position-relative" style={{ zIndex: 2 }}>
        {/* Section Header */}
        <div ref={headerRef} className="text-center mb-5">
          <h2
            className="gradient-text1"
            style={{
              fontSize: "3rem",
              fontWeight: "bold",
              marginBottom: "1rem",
              color: "white",
            }}
          >
            Let's Start a Conversation
          </h2>
          <p
            style={{
              color: "#D1D5DB",
              maxWidth: "700px",
              margin: "0 auto",
              fontSize: "1.125rem",
            }}
          >
            Have a project in mind? We'd love to hear from you. Send us a message
            and we'll respond as soon as possible.
          </p>
        </div>

        {/* Main Card - Floating Between Sections */}
        <Card
          ref={cardRef}
          className="contact-card contact-floating-card"
          style={{
            borderRadius: "2rem",
            border: "1px solid rgba(105, 54, 244, 0.2)",
            background: "rgba(255, 255, 255, 0.98)",
            backdropFilter: "blur(20px)",
            maxWidth: "1000px",
            margin: "0 auto",
            boxShadow: "0 30px 90px rgba(105, 54, 244, 0.3)",
          }}
        >
          <Card.Body style={{ padding: "3rem" }}>
            <Row>
              {/* Left Side - Contact Info */}
              <Col md={5} className="contact-info-column">
                <div className="mb-4">
                  <h4
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "bold",
                      color: "#0F0C1F",
                      marginBottom: "1.5rem",
                    }}
                  >
                    Contact Information
                  </h4>

                  {/* Email Card */}
                  <a
                    href={`mailto:${contactInfo.email}`}
                    ref={(el) => (infoCardsRef.current[0] = el)}
                    className="contact-info-card"
                    style={{ textDecoration: "none" }}
                  >
                    <div className="contact-info-icon">
                      <i className="fa-solid fa-envelope"></i>
                    </div>
                    <div>
                      <h6 style={{ color: "#6936F4", marginBottom: "0.25rem", fontWeight: "600" }}>
                        Email Us
                      </h6>
                      <p style={{ color: "#6B7280", marginBottom: 0, fontSize: "0.9rem" }}>
                        {contactInfo.email}
                      </p>
                    </div>
                  </a>

                  {/* Phone Card */}
                  <a
                    href={`tel:${contactInfo.phone}`}
                    ref={(el) => (infoCardsRef.current[1] = el)}
                    className="contact-info-card"
                    style={{ textDecoration: "none" }}
                  >
                    <div className="contact-info-icon">
                      <i className="fa-solid fa-phone"></i>
                    </div>
                    <div>
                      <h6 style={{ color: "#6936F4", marginBottom: "0.25rem", fontWeight: "600" }}>
                        Call Us
                      </h6>
                      <p style={{ color: "#6B7280", marginBottom: 0, fontSize: "0.9rem" }}>
                        {contactInfo.phoneDisplay}
                      </p>
                    </div>
                  </a>

                  {/* WhatsApp Card */}
                  <a
                    href={`https://wa.me/${contactInfo.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    ref={(el) => (infoCardsRef.current[2] = el)}
                    className="contact-info-card"
                    style={{ textDecoration: "none" }}
                  >
                    <div className="contact-info-icon whatsapp-icon">
                      <i className="fa-brands fa-whatsapp"></i>
                    </div>
                    <div>
                      <h6 style={{ color: "#6936F4", marginBottom: "0.25rem", fontWeight: "600" }}>
                        WhatsApp
                      </h6>
                      <p style={{ color: "#6B7280", marginBottom: 0, fontSize: "0.9rem" }}>
                        Chat with us instantly
                      </p>
                    </div>
                  </a>

                  {/* Location Card */}
                  <a
                    href="https://maps.google.com/?q=Fourways+Golf+Park,+32+Roos+Street,+Fourways,+2191"
                    target="_blank"
                    rel="noopener noreferrer"
                    ref={(el) => (infoCardsRef.current[3] = el)}
                    className="contact-info-card"
                    style={{ textDecoration: "none" }}
                  >
                    <div className="contact-info-icon">
                      <i className="fa-solid fa-location-dot"></i>
                    </div>
                    <div>
                      <h6 style={{ color: "#6936F4", marginBottom: "0.25rem", fontWeight: "600" }}>
                        Visit Us
                      </h6>
                      <p style={{ color: "#6B7280", marginBottom: 0, fontSize: "0.9rem" }}>
                        {contactInfo.address}
                      </p>
                    </div>
                  </a>
                </div>
              </Col>

              {/* Right Side - Contact Form */}
              <Col md={7}>
                <form onSubmit={handleSubmit} className="contact-form">
                  <Row className="mb-3">
                    <Col md={6} className="mb-3 mb-md-0">
                      <div
                        ref={(el) => (formFieldsRef.current[0] = el)}
                        className="form-field-wrapper"
                      >
                        <label className="form-label">Full Name</label>
                        <input
                          name="fullname"
                          type="text"
                          className="form-control contact-input"
                          placeholder="Your Name"
                          value={name}
                          onChange={(event) => setName(event.target.value)}
                          onFocus={() => handleFieldFocus(0)}
                          onBlur={() => handleFieldBlur(0)}
                          required
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div
                        ref={(el) => (formFieldsRef.current[1] = el)}
                        className="form-field-wrapper"
                      >
                        <label className="form-label">Email Address</label>
                        <input
                          name="email"
                          type="email"
                          className="form-control contact-input"
                          placeholder="Your Email Address"
                          value={email}
                          onChange={(event) => setEmail(event.target.value)}
                          onFocus={() => handleFieldFocus(1)}
                          onBlur={() => handleFieldBlur(1)}
                          required
                        />
                      </div>
                    </Col>
                  </Row>

                  <div
                    ref={(el) => (formFieldsRef.current[2] = el)}
                    className="form-field-wrapper mb-4"
                  >
                    <label className="form-label">Your Message</label>
                    <textarea
                      name="message"
                      rows="5"
                      className="form-control contact-input"
                      placeholder="Tell us about your project..."
                      value={message}
                      onChange={(event) => setMessage(event.target.value)}
                      onFocus={() => handleFieldFocus(2)}
                      onBlur={() => handleFieldBlur(2)}
                      required
                    ></textarea>
                  </div>

                  <div
                    ref={(el) => (formFieldsRef.current[3] = el)}
                    style={{ textAlign: "center" }}
                  >
                    <Button
                      type="submit"
                      className="contact-submit-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Sending...
                        </>
                      ) : (
                        <>
                          <span>Send Message</span>
                          <i className="fa-solid fa-paper-plane ms-2"></i>
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Success/Error Messages */}
                  {submitStatus === "success" && (
                    <div className="success-message mt-3">
                      <i className="fa-solid fa-check-circle me-2"></i>
                      Message sent successfully! We'll get back to you soon.
                    </div>
                  )}
                  {submitStatus === "error" && (
                    <div className="error-message mt-3">
                      <i className="fa-solid fa-exclamation-circle me-2"></i>
                      Failed to send message. Please try again.
                    </div>
                  )}
                </form>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>
    </section>
  );
}

export default ContactUs;